import {
  ApplicationQuestion,
  CommonQuestionKeys,
  FieldType,
  SMBGrantCustomQuestionKeys,
  TextInputType,
} from '@bybeam/platform-types';
import { RadioButtonOptions, YesNoOptions } from '@platform-ui/application/utils/index.js';
import { eq } from '../../../ast/utils.js';

type ExtractedCommonQuestionKeys = Extract<CommonQuestionKeys, CommonQuestionKeys.AFFIRMATION>;

export const QUESTIONS: {
  [key in ExtractedCommonQuestionKeys | SMBGrantCustomQuestionKeys]: ApplicationQuestion;
} = {
  [SMBGrantCustomQuestionKeys.BUSINESS_LICENSE]: {
    key: SMBGrantCustomQuestionKeys.BUSINESS_LICENSE,
    copy: {
      title: 'To be eligible for this program, you must maintain an active KCMO business license.',
    },
    fields: [
      {
        type: FieldType.Typography,
        key: 'businessLicense.text',
        copy: 'Please upload a copy of your active KCMO business license.',
        props: {
          variant: 'largeBody',
          alignment: 'center',
        },
      },
      {
        type: FieldType.Document,
        key: SMBGrantCustomQuestionKeys.BUSINESS_LICENSE,
      },
    ],
  },
  [SMBGrantCustomQuestionKeys.TAX_CLEARANCE_LETTER]: {
    key: SMBGrantCustomQuestionKeys.TAX_CLEARANCE_LETTER,
    copy: {
      title: 'To be eligible for this program, you must have a KCMO Tax Clearance Letter.',
    },
    fields: [
      {
        type: FieldType.Typography,
        key: 'taxClearanceLetter.text',
        copy: 'Please upload a copy of your KCMO Tax Clearance Letter',
        props: {
          variant: 'largeBody',
          alignment: 'center',
        },
      },
      {
        type: FieldType.Document,
        key: SMBGrantCustomQuestionKeys.TAX_CLEARANCE_LETTER,
      },
    ],
  },
  [SMBGrantCustomQuestionKeys.BUSINESS_DESCRIPTION]: {
    key: SMBGrantCustomQuestionKeys.BUSINESS_DESCRIPTION,
    copy: {
      title:
        'How would you describe your business? Explain to a potential customer what you provide.',
    },
    fields: [
      {
        type: FieldType.Text,
        key: SMBGrantCustomQuestionKeys.BUSINESS_DESCRIPTION,
        copy: 'How would you describe your business?',
        inputType: TextInputType.Text,
        props: {
          multiline: true,
          rows: 15,
          characterCounter: { max: 1500 },
        },
      },
      {
        type: FieldType.Typography,
        key: 'businessDescription.helper',
        copy: `Please limit the response to 300 words. 

Please Note: This response may be used in printed materials and/or press releases as needed.`,
        props: { variant: 'body', alignment: 'center' },
      },
    ],
  },
  [SMBGrantCustomQuestionKeys.SOCIAL_IMPACT]: {
    key: SMBGrantCustomQuestionKeys.SOCIAL_IMPACT,
    copy: {
      title:
        'What is the social impact of growing your business and how will it change the way you lead the company?',
    },
    fields: [
      {
        type: FieldType.Text,
        key: SMBGrantCustomQuestionKeys.SOCIAL_IMPACT,
        copy: 'What is the social impact of growing your business?',
        inputType: TextInputType.Text,
        props: {
          multiline: true,
          rows: 15,
          characterCounter: { max: 1500 },
        },
      },
      {
        type: FieldType.Typography,
        key: 'socialImpact.helper',
        copy: `Please limit the response to 300 words. 

Please Note: This response may be used in printed materials and/or press releases as needed.`,
        props: { variant: 'label', alignment: 'center' },
      },
    ],
  },
  [SMBGrantCustomQuestionKeys.TEAM_AND_EXPERTISE]: {
    key: SMBGrantCustomQuestionKeys.TEAM_AND_EXPERTISE,
    copy: { title: 'Team and Expertise' },
    fields: [
      {
        type: FieldType.Typography,
        key: 'teamAndExpertise.text',
        copy: 'Introduce key members of your team and their roles.  Include any qualifications and expertise they bring to the business.',
        props: { variant: 'largeBody', alignment: 'center' },
      },
      {
        type: FieldType.Complex,
        key: 'teamAndExpertise.members',
        copy: 'Team Member',
        subFields: [
          {
            type: FieldType.Text,
            inputType: TextInputType.Text,
            copy: 'Name',
            props: { helperText: 'First and last name' },
            key: 'name',
          },
          {
            type: FieldType.Text,
            key: 'expertise',
            copy: 'Qualifications and expertise',
            inputType: TextInputType.Text,
            props: {
              multiline: true,
              rows: 5,
              characterCounter: { max: 500 },
            },
          },
          {
            type: FieldType.Typography,
            key: 'resume.text',
            copy: 'Optional: Upload Resume',
            props: { variant: 'body', alignment: 'left' },
          },
          {
            type: FieldType.Document,
            key: 'resume',
            validation: { required: false },
          },
        ],
      },
      {
        type: FieldType.Typography,
        key: 'owners.text',
        copy: 'Please list any owners of 20% or more of the business by providing their first and last name and percentage of ownership.',
        props: { variant: 'largeBody', alignment: 'center' },
      },
      {
        type: FieldType.Complex,
        key: 'owners',
        copy: 'Owner',
        subFields: [
          {
            type: FieldType.Text,
            inputType: TextInputType.Text,
            copy: 'Name',
            props: { helperText: 'First and last name' },
            key: 'name',
          },
          {
            type: FieldType.Text,
            inputType: TextInputType.Number,
            copy: 'Percentage of ownership',
            key: 'percentageOfOwnership',
            props: { inputProps: { maxlength: 3 } },
          },
          {
            type: FieldType.Typography,
            key: 'resume.text',
            copy: 'Optional: Upload Resume',
            props: { variant: 'body', alignment: 'left' },
          },
          {
            type: FieldType.Document,
            key: 'owners.resume',
            validation: { required: false },
          },
        ],
      },
    ],
  },
  [SMBGrantCustomQuestionKeys.PRIOR_FUNDING]: {
    key: SMBGrantCustomQuestionKeys.PRIOR_FUNDING,
    copy: { title: 'Have you received any grants or COVID-19 relief in the past 3 years?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'priorFunding.priorFunding',
        options: YesNoOptions,
      },
      {
        type: FieldType.Typography,
        key: 'priorFunding.details.text',
        copy: 'Please describe the outcomes and impact.',
        props: {
          variant: 'largeBody',
          alignment: 'center',
        },
        dynamicLogic: eq('priorFunding.priorFunding', 'yes'),
      },
      {
        type: FieldType.Text,
        key: 'priorFunding.details',
        copy: 'Please describe the outcomes and impact.',
        inputType: TextInputType.Text,
        props: {
          multiline: true,
          rows: 5,
          characterCounter: { max: 500 },
        },
        dynamicLogic: eq('priorFunding.priorFunding', 'yes'),
      },
    ],
  },
  [SMBGrantCustomQuestionKeys.MARKET_POTENTIAL]: {
    key: SMBGrantCustomQuestionKeys.MARKET_POTENTIAL,
    layout: 'panel',
    copy: {
      title: 'Describe the market potential for growing your business to the next stage.',
    },
    fields: [
      {
        type: FieldType.Typography,
        key: 'marketPotential.text',
        copy: 'Include your target customers, market size, competition, environmental influences, market barriers, how you differentiate your business in the market, and industry opportunities as applicable.',
        props: { variant: 'largeBody', alignment: 'center' },
      },
      {
        type: FieldType.Text,
        key: SMBGrantCustomQuestionKeys.MARKET_POTENTIAL,
        copy: 'Describe the market potential for growing your business to the next stage.',
        inputType: TextInputType.Text,
        props: {
          multiline: true,
          rows: 10,
          characterCounter: { max: 1000 },
        },
      },
      {
        type: FieldType.Typography,
        key: 'businessDescription.helper',
        copy: 'Please limit the response to 200 words.',
        props: { variant: 'body', alignment: 'center' },
      },
    ],
  },
  [SMBGrantCustomQuestionKeys.HAS_FINANCIAL_STATEMENTS]: {
    key: SMBGrantCustomQuestionKeys.HAS_FINANCIAL_STATEMENTS,
    copy: {
      title:
        'If requested, do you have a summary of your financial statements (profit and loss, balance sheet, previous tax returns)?',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'hasFinancialStatements.hasFinancialStatements',
        options: YesNoOptions,
      },
    ],
  },
  [SMBGrantCustomQuestionKeys.YEARLY_SALES]: {
    key: SMBGrantCustomQuestionKeys.YEARLY_SALES,
    copy: {
      title: 'Please enter your revenue or sales for the years requested.',
    },
    fields: [
      {
        type: FieldType.Typography,
        copy: 'Please note that your financial records may be requested.',
        props: { variant: 'body', alignment: 'center' },
        key: 'yearlySales.helper',
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Currency,
        key: 'yearlySales.from2023',
        copy: '2023',
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Currency,
        key: 'yearlySales.from2022',
        copy: '2022',
        validation: { required: false },
      },
      {
        key: 'yearlySales.yearlySales.label',
        type: FieldType.Typography,
        copy: 'Sales or Revenue (please indicate what you provided above)',
        props: { variant: 'body', alignment: 'center' },
      },
      {
        type: FieldType.Dropdown,
        options: [
          { label: 'Revenue', value: 'revenue' },
          { label: 'Sales', value: 'sales' },
        ],
        key: 'yearlySales.salesOrRevenue',
        copy: 'Please choose one',
      },
    ],
  },
  [SMBGrantCustomQuestionKeys.CASH_FLOW_POSITIVE]: {
    key: SMBGrantCustomQuestionKeys.CASH_FLOW_POSITIVE,
    copy: {
      title: 'Are you cash flow positive?',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'cashflowPositive.cashFlowPositive',
        options: RadioButtonOptions.YesNoUnsure,
      },
    ],
  },
  [SMBGrantCustomQuestionKeys.REVENUE_GOAL]: {
    key: SMBGrantCustomQuestionKeys.REVENUE_GOAL,
    copy: {
      title: 'Revenue Goal',
      intro:
        'What is your revenue goal over the next two to three years?  What existing or new products and/or services do you plan to offer over the next two to three years to achieve your goal?',
    },
    fields: [
      {
        type: FieldType.Text,
        key: SMBGrantCustomQuestionKeys.REVENUE_GOAL,
        copy: 'Please enter your answer here',
        inputType: TextInputType.Text,
        props: {
          multiline: true,
          rows: 5,
          characterCounter: { max: 1000 },
        },
      },
    ],
  },
  [SMBGrantCustomQuestionKeys.CHALLENGES_AND_MITIGATION]: {
    key: SMBGrantCustomQuestionKeys.CHALLENGES_AND_MITIGATION,
    copy: {
      title:
        'What are the primary challenges your business faces, and how do you plan to address them? What risks do you anticipate, and what strategies will you implement to mitigate them?',
    },
    fields: [
      {
        type: FieldType.Text,
        key: SMBGrantCustomQuestionKeys.CHALLENGES_AND_MITIGATION,
        copy: 'Describe your challenges and mitigation strategies',
        inputType: TextInputType.Text,
        props: {
          multiline: true,
          rows: 10,
          characterCounter: { max: 1000 },
        },
      },
      {
        type: FieldType.Typography,
        key: 'challengesAndMitigation.helper',
        copy: 'Please limit the response to 200 words.',
        props: { variant: 'body', alignment: 'center' },
      },
    ],
  },
  [SMBGrantCustomQuestionKeys.BUSINESS_PLAN]: {
    key: SMBGrantCustomQuestionKeys.BUSINESS_PLAN,
    copy: {
      title:
        'Provide a brief overview of your business plan, including your long-term goals and objectives. How do you plan to use the grant funds to achieve your objectives?',
    },
    fields: [
      {
        type: FieldType.Text,
        key: SMBGrantCustomQuestionKeys.BUSINESS_PLAN,
        copy: 'Provide a brief overview of your business plan.',
        inputType: TextInputType.Text,
        props: {
          multiline: true,
          rows: 15,
          characterCounter: { max: 1500 },
        },
      },
      {
        type: FieldType.Typography,
        key: 'businessPlan.helper',
        copy: 'Please limit the response to 300 words.',
        props: { variant: 'body', alignment: 'center' },
      },
    ],
  },
  [SMBGrantCustomQuestionKeys.BUSINESS_PLAN_DOCUMENTS]: {
    key: SMBGrantCustomQuestionKeys.BUSINESS_PLAN_DOCUMENTS,
    copy: {
      title:
        'Please upload any additional documentation or marketing materials that may be applicable to your business plan.',
    },
    fields: [
      {
        type: FieldType.Document,
        key: SMBGrantCustomQuestionKeys.BUSINESS_PLAN_DOCUMENTS,
      },
    ],
    skippable: true,
  },
  [SMBGrantCustomQuestionKeys.CAN_COMMIT_TO_SESSIONS]: {
    key: SMBGrantCustomQuestionKeys.CAN_COMMIT_TO_SESSIONS,
    copy: {
      title: 'Can you commit to a minimum of 10 hours of technical assistance sessions?',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'canCommitToSessions.affirmation',
        options: YesNoOptions,
      },
    ],
  },
  [SMBGrantCustomQuestionKeys.CAN_COMPLETE_MONTHLY_PROGRESS_REPORTS]: {
    key: SMBGrantCustomQuestionKeys.CAN_COMPLETE_MONTHLY_PROGRESS_REPORTS,
    copy: {
      title:
        'Upon accepting the grant funds, you will be required to complete monthly progress reports for up to 90 days after funds have been dispersed. Do you acknowledge this requirement?',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'canCompleteMonthlyProgressReports.affirmation',
        options: YesNoOptions,
      },
    ],
  },
  [SMBGrantCustomQuestionKeys.ANY_CHALLENGES]: {
    key: SMBGrantCustomQuestionKeys.ANY_CHALLENGES,
    copy: {
      title: 'Do you see any challenges with meeting this commitment?',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'anyChallenges.affirmation',
        options: YesNoOptions,
      },
      {
        type: FieldType.Typography,
        key: 'anyChallenges.explanation.text',
        copy: 'Please explain',
        props: {
          variant: 'largeBody',
          alignment: 'center',
        },
        dynamicLogic: eq('anyChallenges.affirmation', 'yes'),
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'anyChallenges.explanation',
        copy: 'Explanation',
        dynamicLogic: eq('anyChallenges.affirmation', 'yes'),
        props: {
          multiline: true,
          rows: 5,
          characterCounter: { max: 500 },
        },
      },
    ],
  },
  [CommonQuestionKeys.AFFIRMATION]: {
    key: CommonQuestionKeys.AFFIRMATION,
    copy: { title: 'Please confirm this statement:' },
    fields: [
      {
        type: FieldType.Checkbox,
        key: CommonQuestionKeys.AFFIRMATION,
        copy: 'I affirm the above to be true and accurate. I understand that financial statements, references, an interview and/or site visit may be necessary for the purposes of determining grant finalists.',
      },
    ],
  },
};
